import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The launch time is stated in Mexico City time (SCRUM-768: "1 de septiembre
 * de 2026 — 8:00 a.m. hora de México"), and the admin editing it may be
 * anywhere. Every conversion in this module goes through this zone so the
 * picker's wall clock always means the same instant for the students.
 */
export const MEXICO_TIMEZONE = "America/Mexico_City";

/** Wall-clock format the picker and the timezone conversion agree on. */
const WALL_CLOCK = "YYYY-MM-DDTHH:mm:ss";

/**
 * Turns a picked date into the ISO instant the API stores.
 *
 * The picker's value carries the BROWSER's timezone, which is not what the
 * admin means: they pick "8:00" thinking of Mexico. So only the wall-clock
 * components are taken, then anchored to Mexico City.
 */
export const toLaunchIso = (value: Dayjs | null): string | null => {
  if (!value) {
    return null;
  }

  return dayjs.tz(value.format(WALL_CLOCK), MEXICO_TIMEZONE).toISOString();
};

/** Turns the stored instant back into a picker value on Mexico City's clock. */
export const fromLaunchIso = (iso: string | null): Dayjs | null => {
  if (!iso) {
    return null;
  }

  const parsed = dayjs(iso);

  // A malformed value from the API must not reach the picker as an Invalid
  // Date, which antd renders as an empty-but-dirty field.
  if (!parsed.isValid()) {
    return null;
  }

  return parsed.tz(MEXICO_TIMEZONE);
};

/** Human-readable launch date for the settings summary. */
export const formatLaunchDate = (iso: string | null): string | null => {
  const value = fromLaunchIso(iso);

  return value ? `${value.format("DD/MM/YYYY HH:mm")} (CDMX)` : null;
};

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Remaining time until the launch, or null once it has passed (or when no
 * launch is configured) — the two cases where the students see no countdown
 * at all.
 */
export const launchCountdownParts = (
  iso: string | null,
  now: Date
): CountdownParts | null => {
  const launch = fromLaunchIso(iso);

  if (!launch) {
    return null;
  }

  const remaining = launch.valueOf() - now.getTime();

  if (remaining <= 0) {
    return null;
  }

  return {
    days: Math.floor(remaining / DAY),
    hours: Math.floor((remaining % DAY) / HOUR),
    minutes: Math.floor((remaining % HOUR) / MINUTE),
    seconds: Math.floor((remaining % MINUTE) / SECOND),
  };
};
